import type { Property } from '@/core/api/models/profile';
import { logger } from '@/core/logger';
import { resolveDynatraceUserIdentity, setDynatraceUserEmail } from '@/core/services/dynatraceService';
import {
  useAppStore,
  type AuthLoginMethod,
  type User,
} from '@/core/stores/appStore';
import { mapUnitToProperty } from '@/modules/auth/utils/mapUnitToProperty';
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from '@/modules/auth/services/profileService';

function buildUserFromProfilePatch(profilePatch: Partial<User>): User {
  const id =
    profilePatch.customer_id?.trim() ||
    profilePatch.id?.trim() ||
    profilePatch.primary_email?.trim() ||
    profilePatch.emairates_id?.trim() ||
    'user';
  return {
    id,
    primary_email: profilePatch.primary_email ?? '',
    ...profilePatch,
  };
}

/**
 * Same store hydration as email/password home `fetchProfile`:
 * GET /v1/customers/profile-details → user + properties.
 *
 * Used for converted-lead (and other normal-dashboard) UAE Pass / Emirates ID
 * sessions that skip the passcode flow.
 */
export async function hydrateNormalCustomerFromProfile(
  jwtToken: string,
  loginMethod: AuthLoginMethod,
  convertedLead: boolean = false,
): Promise<User> {
  const { setAuth, setProfile, setProperties } = useAppStore.getState();

  // Ensure Bearer is available for the profile-details call
  useAppStore.setState((state) => ({
    auth: { ...state.auth, token: jwtToken },
  }));

  logger.info('hydrateNormalCustomerFromProfile:Fetching profile-details', {
    loginMethod,
  });

  const response = await profileService.getProfileDetails();
  const profilePatch = mapProfileDetailsToUserPatch(response);
  const user = buildUserFromProfilePatch(profilePatch);
  const properties: Property[] = (response.units || []).map(mapUnitToProperty);

  setAuth(jwtToken, user, undefined, loginMethod, convertedLead);
  setProfile(profilePatch);
  setProperties(properties);

  const dynatraceUserId = resolveDynatraceUserIdentity(profilePatch);
  if (dynatraceUserId) {
    await setDynatraceUserEmail(dynatraceUserId);
  }

  logger.info('hydrateNormalCustomerFromProfile:Session ready', {
    loginMethod,
    userId: user.id,
    propertiesCount: properties.length,
  });

  return user;
}
